'use client';
import { __rest } from 'tslib';
import React from 'react';
import { keyframes, styled } from '../stitches.config.js';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Cross1Icon } from '@radix-ui/react-icons';
import { overlayStyles } from './Overlay.js';
import { IconButton } from './IconButton.js';

const Sheet = DialogPrimitive.Root;
const SheetTrigger = DialogPrimitive.Trigger;
const fadeIn = keyframes({
    from: { opacity: '0' },
    to: { opacity: '1' },
});
const fadeOut = keyframes({
    from: { opacity: '1' },
    to: { opacity: '0' },
});
const StyledOverlay = styled(DialogPrimitive.Overlay, overlayStyles, {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: '$2',
    '&[data-state="open"]': {
        animation: `${fadeIn} 150ms cubic-bezier(0.22, 1, 0.36, 1)`,
    },
    '&[data-state="closed"]': {
        animation: `${fadeOut} 150ms cubic-bezier(0.22, 1, 0.36, 1)`,
    },
});
const slideIn = keyframes({
    from: { transform: '$$transformValue' },
    to: { transform: 'translate3d(0,0,0)' },
});
const slideOut = keyframes({
    from: { transform: 'translate3d(0,0,0)' },
    to: { transform: '$$transformValue' },
});
const StyledContent = styled(DialogPrimitive.Content, {
    backgroundColor: '$panel',
    boxShadow: '$colors$shadowLight 0 0 38px -10px, $colors$shadowDark 0 0 35px -15px',
    position: 'fixed',
    top: 0,
    bottom: 0,
    width: 250,
    zIndex: '$3',
    // Among other things, prevents text alignment inconsistencies when dialog can't be centered in the viewport evenly.
    // Affects animated and non-animated dialogs alike.
    willChange: 'transform',
    // '&:focus': {
    //   outline: 'none',
    // },
    '&[data-state="open"]': {
        animation: `${slideIn} 150ms cubic-bezier(0.22, 1, 0.36, 1)`,
    },
    '&[data-state="closed"]': {
        animation: `${slideOut} 150ms cubic-bezier(0.22, 1, 0.36, 1)`,
    },
    variants: {
        side: {
            top: {
                $$transformValue: 'translate3d(0,-100%,0)',
                width: '100%',
                height: 300,
                bottom: 'auto',
            },
            right: {
                $$transformValue: 'translate3d(100%,0,0)',
                right: 0,
            },
            bottom: {
                $$transformValue: 'translate3d(0,100%,0)',
                width: '100%',
                height: 300,
                bottom: 0,
                top: 'auto',
            },
            left: {
                $$transformValue: 'translate3d(-100%,0,0)',
                left: 0,
            },
        },
    },
    defaultVariants: {
        side: 'right',
    },
});
const StyledCloseButton = styled(DialogPrimitive.Close, {
    position: 'absolute',
    top: '$2',
    right: '$2',
});
const SheetContent = React.forwardRef((_a, forwardedRef) => {
    var { children, dialogBrightness } = _a, props = __rest(_a, ["children", "dialogBrightness"]);
    return (React.createElement(DialogPrimitive.Portal, null,
        React.createElement(StyledOverlay, { brightness: dialogBrightness }),
        React.createElement(StyledContent, Object.assign({}, props, { ref: forwardedRef }),
            children,
            React.createElement(StyledCloseButton, { asChild: true },
                React.createElement(IconButton, { variant: "ghost" },
                    React.createElement(Cross1Icon, null))))));
});
const SheetClose = DialogPrimitive.Close;
const SheetTitle = DialogPrimitive.Title;
const SheetDescription = DialogPrimitive.Description;

export { Sheet, SheetClose, SheetContent, SheetDescription, SheetTitle, SheetTrigger };
